import { Component, useEffect, useState, type ErrorInfo, type ReactNode } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowRight, FileDown, AlertTriangle, ExternalLink } from "lucide-react";
import type { DynamicFieldKey } from "@/hooks/useDocumentTemplates";
import { supabase } from "@/integrations/supabase/client";

type Values = Partial<Record<DynamicFieldKey, string>>;
type Originals = Partial<Record<DynamicFieldKey, string>>;
export type CoverOpenIn = "download" | "word" | "google";
export interface CoverFieldsResult {
  values: Values;
  openIn: CoverOpenIn;
}
type Resolver = (v: CoverFieldsResult | null) => void;
interface OpenEvent {
  fields: DynamicFieldKey[];
  defaults: Values;
  originals?: Originals;
  filename?: string;
  format?: "pdf" | "docx";
  resolve: Resolver;
}

const EVENT_NAME = "thiia:cover-fields:open";

export function openCoverFieldsDialog(
  fields: DynamicFieldKey[],
  defaults: Values,
  meta?: { originals?: Originals; filename?: string; format?: "pdf" | "docx" },
): Promise<CoverFieldsResult | null> {
  return new Promise((resolve) => {
    window.dispatchEvent(new CustomEvent<OpenEvent>(EVENT_NAME, {
      detail: { fields, defaults, resolve, ...(meta || {}) },
    }));
  });
}

const LABELS: Record<DynamicFieldKey, string> = {
  title: "Titre", client: "Client", date: "Date", author: "Auteur", company: "Société",
};

// Error boundary so a runtime error (e.g. a missing context provider)
// degrades gracefully instead of taking down the whole app.
class CoverFieldsErrorBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  state = { error: null as Error | null };
  static getDerivedStateFromError(error: Error) { return { error }; }
  componentDidCatch(error: Error, info: ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error("[CoverFieldsHost] crashed:", error, info);
  }
  reset = () => this.setState({ error: null });
  render() {
    if (!this.state.error) return this.props.children;
    return (
      <Dialog open onOpenChange={(o) => { if (!o) this.reset(); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <AlertTriangle className="w-4 h-4 text-amber-600" />
              Aperçu indisponible
            </DialogTitle>
            <DialogDescription>
              Une erreur a empêché l'affichage de l'aperçu des remplacements.
              Vous pouvez continuer à télécharger le document sans aperçu.
            </DialogDescription>
          </DialogHeader>
          <p className="text-[13px] text-muted-foreground font-mono break-all">
            {this.state.error?.message}
          </p>
          <DialogFooter>
            <Button variant="ghost" onClick={this.reset}>Fermer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }
}

function CoverFieldsHostInner() {
  const [state, setState] = useState<OpenEvent | null>(null);
  const [values, setValues] = useState<Values>({});
  const [googleConnected, setGoogleConnected] = useState(false);
  const [hasDriveScope, setHasDriveScope] = useState(false);
  const [wordConnected, setWordConnected] = useState(false);

  // Keep this global host independent from AuthProvider: it may be mounted early
  // and only needs a best-effort connection status for the current Supabase user.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (state?.format !== "docx") {
        setWordConnected(false);
        setGoogleConnected(false);
        setHasDriveScope(false);
        return;
      }

      const { data: sessionData } = await supabase.auth.getSession();
      const userId = sessionData.session?.user?.id;
      if (!userId) {
        setWordConnected(false);
        setGoogleConnected(false);
        setHasDriveScope(false);
        return;
      }

      const [wordResult, googleResult] = await Promise.all([
        supabase.from("microsoft_tokens").select("id").eq("user_id", userId).limit(1),
        supabase.from("google_tokens").select("scopes").eq("user_id", userId).limit(1),
      ]);

      if (cancelled) return;
      const googleRows = googleResult.data || [];
      const scopes = (googleRows[0]?.scopes as string | null) || "";
      setWordConnected(!!(wordResult.data && wordResult.data.length > 0));
      setGoogleConnected(googleRows.length > 0);
      setHasDriveScope(scopes.includes("drive.file"));
    };
    load();
    return () => { cancelled = true; };
  }, [state?.format]);

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<OpenEvent>).detail;
      if (!detail) return;
      setState(detail);
      setValues({ ...detail.defaults });
    };
    window.addEventListener(EVENT_NAME, handler as EventListener);
    return () => window.removeEventListener(EVENT_NAME, handler as EventListener);
  }, []);

  const close = (v: CoverFieldsResult | null) => { state?.resolve(v); setState(null); setValues({}); };
  const open = !!state;
  const isDocx = state?.format === "docx";
  const fmtLabel = isDocx ? "Word" : state?.format === "pdf" ? "PDF" : "le document";
  const changeCount = (state?.fields || []).filter((fk) => (values[fk] || "").trim().length > 0).length;

  const showOnlineOptions = isDocx && (wordConnected || (googleConnected && hasDriveScope));

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) close(null); }}>
      <DialogContent className="max-w-lg max-h-[90vh] flex flex-col overflow-hidden">
        <DialogHeader>
          <DialogTitle>Aperçu des remplacements</DialogTitle>
          <DialogDescription>
            Vérifiez ce qui sera injecté dans la garde avant de télécharger {fmtLabel}
            {state?.filename ? <> — <span className="font-mono text-[13px]">{state.filename}</span></> : null}.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2.5 flex-1 min-h-0 overflow-y-auto pr-1">
          {(state?.fields || []).map((fk) => {
            const original = state?.originals?.[fk]?.trim();
            const missingSource = !original;
            const value = values[fk] || "";
            return (
              <div key={fk} className="space-y-1 p-2 rounded-md border bg-muted/30">
                <div className="flex items-center justify-between gap-2">
                  <Label className="text-xs font-medium">{LABELS[fk]}</Label>
                  {original ? (
                    <span className="text-[13px] text-muted-foreground truncate max-w-[200px]" title={original}>
                      Avant : « {original.length > 32 ? original.slice(0, 32) + "…" : original} »
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-[13px] text-amber-700" title="Aucun texte d'origine détecté pour cette zone : le remplacement risque d'être ignoré.">
                      <AlertTriangle className="w-3 h-3" /> zone sans texte source
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-1.5">
                  <ArrowRight className="w-3 h-3 text-muted-foreground shrink-0" />
                  <Input
                    value={value}
                    onChange={(e) => setValues({ ...values, [fk]: e.target.value })}
                    placeholder="(laisser vide pour ne rien remplacer)"
                    className="h-9 text-sm"
                  />
                </div>
                {missingSource && value.trim() && (
                  <div className="text-[13px] text-amber-700 pl-4">
                    Cette zone n'a pas de texte d'origine — le remplacement peut être ignoré côté Word.
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="text-[13px] text-muted-foreground border-t pt-2">
          {changeCount > 0
            ? `${changeCount} remplacement${changeCount > 1 ? "s" : ""} seront appliqués à l'export.`
            : "Aucun remplacement — la garde d'origine sera utilisée telle quelle."}
        </div>

        <DialogFooter className="gap-2 flex-wrap sm:flex-nowrap">
          <Button variant="ghost" onClick={() => close(null)}>Annuler</Button>
          <Button onClick={() => close({ values, openIn: "download" })}>
            <FileDown className="w-3.5 h-3.5 mr-1.5" />
            Confirmer et télécharger
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function CoverFieldsHost() {
  return (
    <CoverFieldsErrorBoundary>
      <CoverFieldsHostInner />
    </CoverFieldsErrorBoundary>
  );
}
